// Answers of the cosmos API, parsed with zod: the contract has no response schemas yet, so these
// follow the platform's views (cosmosai_platform/queries.py, outcomes.py), camelCase on the wire.
// The Studio parses with them and the mock (apps/cosmos-mock) answers with them (ADR 0017).
import { z } from "zod";

export const State = z.enum(["draft", "published", "deprecated"]);
export type State = z.infer<typeof State>;

export const Tier = z.enum(["development", "staging", "production"]);
export type Tier = z.infer<typeof Tier>;

/** A workspace, team or project, with its latest version. */
export const ScopeView = z.object({
  kind: z.string(),
  name: z.string(),
  version: z.number().int(),
  displayName: z.string().nullable(),
  team: z.string().nullable().optional(),
});
export type ScopeView = z.infer<typeof ScopeView>;

export const VersionView = z.object({
  version: z.number().int(),
  state: State,
  digest: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  publishedAt: z.string().nullable(),
  /** Names of the environments running this version. */
  deployedIn: z.array(z.string()),
});
export type VersionView = z.infer<typeof VersionView>;

/** A spec object and its versions, oldest first; `project` is "" for workspace-level kinds. */
export const ObjectView = z.object({
  ref: z.string(),
  kind: z.string(),
  project: z.string(),
  name: z.string(),
  versions: z.array(VersionView),
});
export type ObjectView = z.infer<typeof ObjectView>;

/** One version's content: the parsed document and the YAML it was applied from. */
export const DocumentView = z.object({
  ref: z.string(),
  state: State,
  digest: z.string(),
  document: z.record(z.string(), z.unknown()),
  sourceYaml: z.string(),
});
export type DocumentView = z.infer<typeof DocumentView>;

export const EnvironmentView = z.object({
  name: z.string(),
  tier: Tier,
  version: z.number().int(),
});
export type EnvironmentView = z.infer<typeof EnvironmentView>;

/** A version running in an environment: `ref` carries `@N`. */
export const DeploymentView = z.object({ ref: z.string(), state: State });
export type DeploymentView = z.infer<typeof DeploymentView>;

export const EventAction = z.enum(["apply", "promote", "rollback", "discard"]);
export type EventAction = z.infer<typeof EventAction>;

/** What moved in an environment; the history lists them oldest first. */
export const EventView = z.object({
  at: z.string(),
  by: z.string(),
  action: EventAction,
  ref: z.string(),
  fromVersion: z.number().int().nullable(),
  toVersion: z.number().int().nullable(),
});
export type EventView = z.infer<typeof EventView>;

/** One finding of the platform (spec-kit's Problem), on success (warnings) or in a refusal. */
export const SpecProblem = z.object({
  code: z.string(),
  level: z.string(),
  severity: z.enum(["error", "warning"]),
  path: z.string(),
  line: z.number().int(),
  column: z.number().int(),
  pointer: z.string(),
  message: z.string(),
  hint: z.string().nullable().optional(),
});
export type SpecProblem = z.infer<typeof SpecProblem>;

export const Change = z.object({
  ref: z.string(),
  change: z.enum(["created", "updated", "unchanged", "deleted"]),
});
export type Change = z.infer<typeof Change>;

/** What a registry command changed, and its warnings. */
export const Outcome = z.object({ changes: z.array(Change), problems: z.array(SpecProblem) });
export type Outcome = z.infer<typeof Outcome>;

/** The findings of a refusal's problem document, or none when it lists none we can read. */
export function problemsOf(body: unknown): SpecProblem[] {
  const parsed = z.object({ problems: z.array(SpecProblem) }).safeParse(body);
  return parsed.success ? parsed.data.problems : [];
}

/** An answer whose shape differs from the platform's views: named by its endpoint. */
export class UnexpectedAnswerError extends Error {
  override name = "UnexpectedAnswerError";
  readonly endpoint: string;

  constructor(endpoint: string, issues: string) {
    super(`${endpoint}: ${issues}`);
    this.endpoint = endpoint;
  }
}

/** Parses an answer, or throws an UnexpectedAnswerError naming the endpoint and the fields. */
export function parseAnswer<S extends z.ZodType>(schema: S, endpoint: string, body: unknown) {
  const parsed = schema.safeParse(body);
  if (parsed.success) return parsed.data as z.infer<S>;
  const issues = parsed.error.issues
    .slice(0, 3)
    .map((issue) => `${issue.path.join(".") || "(root)"} ${issue.message}`)
    .join("; ");
  throw new UnexpectedAnswerError(endpoint, issues);
}

/** The newest version of a spec object (versions come oldest first). */
export const latestVersion = (object: ObjectView): VersionView | undefined =>
  object.versions.reduce<VersionView | undefined>(
    (latest, version) => (!latest || version.version > latest.version ? version : latest),
    undefined,
  );

/** The version of a spec object running in an environment, if any. */
export const versionIn = (object: ObjectView, env: string): VersionView | undefined =>
  object.versions.find((version) => version.deployedIn.includes(env));
